import { FunctionComponent, useEffect, useMemo, useState } from 'react';

import { Stack, Typography } from '@mui/material';

import { useFileUploader, useScreenSize, useSnackbar } from '@dw/hooks';

import { Button, Form, FormTextInput, Avatar } from '@dw/components/core';

import { IDeviceFile } from '@dw/services/filePickerService';
import { useCreateDanceSchoolMutation } from '@dw/hooks/api/DanceSchoolApi';
import { IDanceSchool } from '@dw/models/danceSchool';
import { useCreateBlobUploadLinkMutation } from '@dw/hooks/api/BlobApi';
import { BLOB_UPLOAD_TYPE } from '@dw/models/blob';
import { DEFAULT_ERROR_MESSAGE, SCREEN_SIZE } from '@dw/constants';
import { useSwitchSchoolsMutation } from '@dw/hooks/api/AuthApi';
import { DEFAULT_COUNTRY_CODE } from '@dw/constants/intl';

interface ICreateDanceSchoolForm {
  name: string;
  websiteUrl: string;
}
const initFormData = (): ICreateDanceSchoolForm => ({
  name: '',
  websiteUrl: ''
});

interface IQuickCreateDanceSchoolFormProps {
  loadingStatusChanged?: (isLoading: boolean) => void;
  onSaved?: (result: IDanceSchool) => void;
}
export const QuickCreateDanceSchoolForm: FunctionComponent<IQuickCreateDanceSchoolFormProps> = (props: IQuickCreateDanceSchoolFormProps) => {

  const { onSaved, loadingStatusChanged } = props;

  const { showError } = useSnackbar();
  const { currentSize } = useScreenSize();

  const [formData, setFormData] = useState<ICreateDanceSchoolForm>(initFormData());
  const [logo, setLogo] = useState<IDeviceFile | null>(null);

  const { uploadFile, isUploadingFile } = useFileUploader();

  const { mutateAsync: createBlobUploadLink, isPending: createBlobUploadLinkLoading } = useCreateBlobUploadLinkMutation();
  const { mutateAsync: createDanceSchool, isPending: createDanceSchoolLoading } = useCreateDanceSchoolMutation();

  const { mutate: switchSchools, isPending: switchSchoolsLoading } = useSwitchSchoolsMutation();

  const isLoading = useMemo(() => {
    return isUploadingFile || createBlobUploadLinkLoading || createDanceSchoolLoading || switchSchoolsLoading;
  }, [isUploadingFile, createBlobUploadLinkLoading, createDanceSchoolLoading, switchSchoolsLoading]);

  const handleSubmit = async (formData: ICreateDanceSchoolForm) => {
    if (loadingStatusChanged) {
      loadingStatusChanged(true);
    }

    let logoKey = '';
    if (logo && logo?.rawFile) {
      const uploadLink = await createBlobUploadLink({
        uploadType: BLOB_UPLOAD_TYPE.DANCE_SCHOOL_LOGO,
        fileName: logo.fileName,
        contentType: logo.contentType
      });

      const uploadResult = await uploadFile(uploadLink.signedUrl, logo.rawFile, uploadLink.metadata);
      if (!uploadResult) {
        showError(DEFAULT_ERROR_MESSAGE);
        return;
      }
      logoKey = uploadLink.key;
    }

    const newSchool = await createDanceSchool({
      name: formData.name,
      logoKey: logoKey,
      countryCode: DEFAULT_COUNTRY_CODE,
      websiteUrl: formData.websiteUrl
    });

    switchSchools({
      danceSchoolId: newSchool.id
    });

    if (onSaved) {
      onSaved(newSchool);
    }
  }

  useEffect(() => {
    setLogo(null);
    setFormData(initFormData());
  }, []);

  useEffect(() => {
    if (loadingStatusChanged) {
      loadingStatusChanged(isLoading);
    }
  }, [isLoading, loadingStatusChanged]);

  return (
    <Form
      formData={formData}
      formDataChanged={setFormData}
      onSubmit={handleSubmit}
      disabled={isLoading}
    >
      <Stack direction='column' rowGap={0}>
        <FormTextInput<ICreateDanceSchoolForm>
          name='name'
          label='Dance School Name'
          maxLength={30}
          required
        />

        <Stack direction='column' rowGap={2} marginY={3} justifyContent='center' alignItems='center'>
          <Typography>Upload your logo</Typography>

          <Avatar
            canEdit
            disabled={isLoading}
            shape='square'
            size={currentSize < SCREEN_SIZE.XL ? 'medium' : 'large'}
            onNewPictureSelected={setLogo}
            addImageText='Add Logo'
          />
        </Stack>

        <FormTextInput<ICreateDanceSchoolForm>
          name='websiteUrl'
          maxLength={250}
          type='url'
          required
          helperText='Should start with https://'
        />

        <Stack marginTop={2}>
          <Button
            type='submit'
            loading={isLoading}
          >
            Create Dance School
          </Button>
        </Stack>
      </Stack>
    </Form>
  )
}